import React, { useMemo, useState } from 'react';
import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { analyzeReading, ReadingAnalysisResponse, ReadingHighlight } from '../utils/api';
import { escapeRegExp } from '../utils/text';

const LANGUAGE_OPTIONS = ['EN', 'ZH', 'AR', 'FR', 'DE', 'ES', 'RU', 'JA', 'KO'];

function highlightText(text: string, highlights: ReadingHighlight[]) {
  const words = highlights.map((item) => item.word).filter(Boolean);
  if (!words.length) {
    return text;
  }

  const escaped = words.map((word) => escapeRegExp(word));
  const parts = text.split(new RegExp(`(${escaped.join('|')})`, 'gi'));

  return parts.map((part, index) => {
    const match = words.find((word) => word.toLowerCase() === part.toLowerCase());
    if (!match) {
      return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
    }
    return (
      <Box component="span" key={`${part}-${index}`} sx={{ bgcolor: 'rgba(8,145,178,0.12)', color: '#0f766e', borderBottom: '2px solid #0891b2', fontWeight: 700 }}>
        {part}
      </Box>
    );
  });
}

function normalizeReadingResponse(inputText: string, payload: ReadingAnalysisResponse): ReadingAnalysisResponse {
  const highlights = payload.vocab_highlights?.length
    ? payload.vocab_highlights
    : [
        { word: 'nuance', part_of_speech: 'noun', definition: 'A subtle difference in meaning.', difficulty: 'LEARNING' },
        { word: 'semantic', part_of_speech: 'adj', definition: 'Related to meaning in language.', difficulty: '90% MATCH' },
      ];

  const quizQuestions = payload.quiz_questions?.length
    ? payload.quiz_questions
    : [
        {
          question: 'Which idea best captures the passage tone?',
          options: ['Purely literal', 'Context-sensitive', 'Emotionless'],
          answer: 'Context-sensitive',
          explanation: 'The text emphasizes shades of meaning rather than literal translation.',
        },
        {
          question: 'What does “nuance” suggest in the passage?',
          options: ['A fixed meaning', 'A subtle variation', 'A grammar rule'],
          answer: 'A subtle variation',
          explanation: 'Nuance refers to subtle shifts in meaning or tone.',
        },
        {
          question: 'Why are highlighted words useful?',
          options: ['They are decorative only', 'They mark meaningful vocabulary', 'They remove ambiguity completely'],
          answer: 'They mark meaningful vocabulary',
          explanation: 'The highlights surface words worth learning or revisiting.',
        },
      ];

  return {
    title: payload.title || 'Paste your text for analysis',
    category: payload.category || 'FRENCH CLASSICS',
    difficulty: payload.difficulty || 'INTERMEDIATE',
    word_count: payload.word_count || inputText.trim().split(/\s+/).filter(Boolean).length,
    original_text: payload.original_text || inputText,
    summary: payload.summary || 'This passage explores meaning through tone, context, and vocabulary choice.',
    vocab_highlights: highlights,
    grammar_notes: payload.grammar_notes?.length
      ? payload.grammar_notes
      : [
          {
            title: 'Context framing',
            sentence: 'A phrase may appear simple, but its context changes the meaning.',
            explanation: 'Notice how the surrounding sentence shapes interpretation.',
          },
        ],
    quiz_questions: quizQuestions,
  };
}

export default function ReadingPage() {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [language, setLanguage] = useState('FR');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<ReadingAnalysisResponse | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});

  const handleAnalyze = async () => {
    if (!text.trim()) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await analyzeReading(text, language.toLowerCase());
      setResult(normalizeReadingResponse(text, response));
      setCurrentQuestion(0);
      setAnswers({});
    } catch (analysisError) {
      setError(analysisError instanceof Error ? analysisError.message : 'Unable to analyze text.');
    } finally {
      setLoading(false);
    }
  };

  const quiz = result?.quiz_questions || [];
  const activeQuestion = quiz[currentQuestion];
  const progress = quiz.length ? ((currentQuestion + 1) / quiz.length) * 100 : 0;
  const selectedAnswer = answers[currentQuestion];
  const isCorrect = activeQuestion ? selectedAnswer === activeQuestion.answer : false;

  const vocabHighlights = useMemo(() => result?.vocab_highlights || [], [result?.vocab_highlights]);

  return (
    <Stack spacing={3}>
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Stack spacing={2}>
            <Typography sx={{ color: '#6b7280', fontWeight: 700 }}>LIBRARY &gt; {result?.category || 'FRENCH CLASSICS'}</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>{result?.title || 'Paste your text for analysis'}</Typography>
              <Stack direction="row" spacing={1}>
                <Chip label={result?.difficulty || 'INTERMEDIATE'} sx={{ bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 700 }} />
                <Chip label={`${result?.word_count || text.trim().split(/\s+/).filter(Boolean).length || 0} words`} variant="outlined" />
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {!result && (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stack spacing={2.5}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField
                multiline
                minRows={10}
                value={text}
                onChange={(event) => setText(event.target.value)}
                placeholder="Paste reading material here to generate summary, vocabulary insights, and a quick check quiz..."
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <TextField select label="Language" value={language} onChange={(event) => setLanguage(event.target.value)} sx={{ minWidth: 180 }}>
                  {LANGUAGE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </TextField>
                <Button variant="contained" onClick={handleAnalyze} disabled={loading} sx={{ bgcolor: '#111827', '&:hover': { bgcolor: '#1f2937' } }}>
                  {loading ? 'Analyzing...' : 'Analyze Text'}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {result && (
        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} alignItems="flex-start">
          <Card sx={{ flex: 1.15, width: '100%', borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>Original Text</Typography>
                <Stack direction="row" spacing={1}>
                  <AutoStoriesRoundedIcon sx={{ color: '#6b7280' }} />
                  <TranslateRoundedIcon sx={{ color: '#6b7280' }} />
                </Stack>
              </Stack>
              <Typography sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.9, color: '#111827' }}>
                {highlightText(result.original_text || '', vocabHighlights)}
              </Typography>
            </CardContent>
          </Card>

          <Stack spacing={3} sx={{ flex: 1, width: '100%' }}>
            <AccentPanel title="✨ AI SUMMARY" accent="#0891b2">
              <Typography color="text.secondary">{result.summary}</Typography>
            </AccentPanel>

            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>VOCABULARY RADAR</Typography>
                  <Chip label={`${vocabHighlights.length} New Words`} sx={{ bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 700 }} />
                </Stack>
                <Stack spacing={1.75}>
                  {vocabHighlights.map((item) => (
                    <Box key={item.word} sx={{ p: 2, borderRadius: 2.5, bgcolor: '#f8fafc' }}>
                      <Stack direction="row" justifyContent="space-between" spacing={1.5} alignItems="center">
                        <Typography component="code" sx={{ fontSize: 16, fontWeight: 700 }}>{item.word}</Typography>
                        <Chip label={item.difficulty || 'LEARNING'} size="small" sx={{ bgcolor: '#e0f2fe', color: '#0369a1' }} />
                      </Stack>
                      <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                        {item.part_of_speech ? `${item.part_of_speech} · ` : ''}{item.definition || 'Meaning surfaced from the passage context.'}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>

            <AccentPanel title="✂ GRAMMAR LAB" accent="#14b8a6">
              <Stack spacing={2}>
                {result.grammar_notes?.map((item) => (
                  <Box key={item.title}>
                    <Typography sx={{ fontWeight: 800, mb: 0.75 }}>{item.title}</Typography>
                    <Typography sx={{ color: '#111827', mb: 0.75 }}>
                      {highlightText(item.sentence, vocabHighlights)}
                    </Typography>
                    <Typography color="text.secondary">{item.explanation}</Typography>
                  </Box>
                ))}
              </Stack>
            </AccentPanel>

            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>📋 QUICK CHECK</Typography>
                <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 999, mb: 2.5, bgcolor: '#e5e7eb', '& .MuiLinearProgress-bar': { bgcolor: '#1a73e8' } }} />
                {activeQuestion && (
                  <Stack spacing={2}>
                    <Typography sx={{ fontWeight: 700 }}>Question {currentQuestion + 1}/{quiz.length}</Typography>
                    <Typography>{activeQuestion.question}</Typography>
                    <RadioGroup value={selectedAnswer || ''} onChange={(event) => setAnswers((current) => ({ ...current, [currentQuestion]: event.target.value }))}>
                      {activeQuestion.options.map((option) => {
                        const chosen = selectedAnswer === option;
                        const correct = activeQuestion.answer === option;
                        const color = chosen ? (correct ? '#16a34a' : '#dc2626') : '#111827';
                        return (
                          <Box key={option} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, px: 1.5, py: 0.75, mb: 1 }}>
                            <FormControlLabel value={option} control={<Radio />} label={<Box component="span" sx={{ color }}>{option}</Box>} />
                          </Box>
                        );
                      })}
                    </RadioGroup>
                    {selectedAnswer && (
                      <Alert severity={isCorrect ? 'success' : 'error'}>
                        {activeQuestion.explanation}
                      </Alert>
                    )}
                    <Button
                      variant="outlined"
                      endIcon={<ChevronRightRoundedIcon />}
                      disabled={!selectedAnswer || currentQuestion === quiz.length - 1}
                      onClick={() => setCurrentQuestion((current) => current + 1)}
                    >
                      Next question
                    </Button>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Stack>
      )}

      {result && (
        <Button variant="contained" size="large" onClick={() => navigate('/chat')} sx={{ py: 1.7, bgcolor: '#111827', '&:hover': { bgcolor: '#1f2937' } }}>
          💬 Start AI Chat Practice
        </Button>
      )}
    </Stack>
  );
}

function AccentPanel({ title, accent, children }: { title: string; accent: string; children: React.ReactNode }) {
  return (
    <Card sx={{ borderRadius: 3, borderLeft: `6px solid ${accent}` }}>
      <CardContent sx={{ p: 3.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>{title}</Typography>
        {children}
      </CardContent>
    </Card>
  );
}
